import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {TicketService} from '@app/core/services/ticket.service';
import {ActivityComponent} from '@app/maintenance/components/activity/activity.component';
import {TasksService} from '@app/maintenance/service/tasks.service';
import {TranslateParser, TranslateService} from '@ngx-translate/core';
import {ConfirmationModalComponent, PdfViewerComponent} from '@app/shared/components';

import * as FileSaver from 'file-saver';
import {ToastsManager} from "@app/shared/util/toastr.service";
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {taskUnits} from '@app/shared/models/consts/taskUnits.const';
import {AppUtils} from '@app/shared/util/app.utils';
import {TicketOperationStatus} from '@app/shared/models/enums/ticket-operation-status.enum';
import {TicketOperationTaskStatus} from '@app/shared/models/enums/ticket-operation-task-status.enum';
import {TicketCreateModalComponent} from '@app/roles/admin/pages/ticket-management/components';
import {TableConfig} from '@app/shared/util/table-config.utils';
import {TaskInstructionType} from '@app/shared/models/enums/task-instruction-type.enum';
import {AuthenticationService} from '@app/authentication/services/authentication.service';
import {TicketOperationTask} from '@app/shared/models/ticket-operation-tasks.model';
import {DateTimeUtils} from '@app/shared/util/date-time.utils';
import {isEmpty} from "lodash";
import {EventType} from "@app/shared/models/enums/eventType.enum";
import {NextTicketOperation} from '@app/shared/models/next-ticket-operation.model';
import {RevisionInfo} from "@components/revision-info/models/revision-info.model";
import {RevisionInfoModalComponent} from "@components/revision-info-modal/revision-info-modal.component";
import {ToolService} from "@services/tool.service";
import {Log} from "ng2-logger";

@Component({
    selector: 'app-ticket-activity-details',
    templateUrl: './ticket-activity-details.component.html',
    styleUrls: ['./ticket-activity-details.component.scss']
})
export class TicketActivityDetailsComponent implements OnInit {
    @ViewChild('confirmSelection', {static: false}) confirmationModal: ConfirmationModalComponent;
    @ViewChild('scanBadge', {static: false}) badgeConfirmationModal: ConfirmationModalComponent;
    @ViewChild('markTasksDone', {static: false}) markTaskDoneModal: ConfirmationModalComponent;
    @ViewChild('taskObservation', {static: false}) taskObservationModal: ConfirmationModalComponent;
    @ViewChild('taskSpareQuantity', {static: false}) taskSpareQuantityModal: ConfirmationModalComponent;
    @ViewChild('markTasksNotDone', {static: false}) markTasksNotDoneModal: ConfirmationModalComponent;
    @ViewChild('notDoneReason', {static: false}) notDoneReasonModal: ConfirmationModalComponent;
    @ViewChild('finishGroup', {static: false}) finishGroupModal: ConfirmationModalComponent;
    @ViewChild('scanBadgeForReturn', {static: false}) badgeForReturnConfirmationModal: ConfirmationModalComponent;

    @ViewChild(TicketCreateModalComponent, {static: false}) ticketCreateModal: TicketCreateModalComponent;
    @ViewChild(PdfViewerComponent) pdfViewerModal: PdfViewerComponent;
    @ViewChild(RevisionInfoModalComponent, {static: false}) operationRevisionInfoModal: RevisionInfoModalComponent;
    @ViewChild('groupComplete', {static: false}) groupCompleteConfirmationModal: ConfirmationModalComponent;
    @ViewChild('operation', {static: false}) operation: ActivityComponent;

    loading = false;
    loadingTask = false;
    activityId: number;
    activity: any;
    ticketId: number;
    selectedToolId: number;
    ticket: any;
    groupings: any[];
    selectedTicketTaskId: number;
    selectedTask: TicketOperationTask;
    selectedTasks = [];
    comment: string;
    form: UntypedFormGroup
    spareQuantity: number;
    showValidations = false;
    boschId: string;
    notDoneReason: string;
    nextTaskAction: string;
    finishedGroups = [];
    openNewTicket = false;
    nextOperations: NextTicketOperation[];

    readonly: boolean;
    tableConfig: TableConfig;

    groupCompletedMessage = '';

    selectedGroupForReturn: any;

    _logger = Log.create(TicketActivityDetailsComponent.name);

    constructor(private route: ActivatedRoute,
                private ticketService: TicketService,
                private taskService: TasksService,
                private translate: TranslateService,
                private toastr: ToastsManager,
                private translateParser: TranslateParser,
                private authService: AuthenticationService) {
    }

    ngOnInit() {
        this.loading = true;
        this.readonly = !this.authService.hasPermission('PROCESS_TICKETS');
        this.route.params.subscribe(
            (params) => {
                this.activityId = +params['activityId'];
                this.ticketId = +this.route.snapshot.parent.params['id'];
                this.selectedToolId = +this.route.snapshot.queryParams['toolId'];
                this.getTicketDetails(this.ticketId);
            }
        );

        this.form = new UntypedFormGroup({
            'spareQuantity': new UntypedFormControl(this.spareQuantity, Validators.required),
            'newTicket': new UntypedFormControl(false)
        });
    }

    getTicketDetails(ticketId: number) {
        this.loading = true;
        this.ticketService.getTicketById(ticketId).subscribe(
            (data) => {
                this.ticket = data;
                this.activity = this.ticket.ticketOperations.find(item => item.operationId === this.activityId);
                this.getTicketOperationDetails(ticketId);
            },
            (error) => {
                this._logger.error("Error getting ticket details", error);
                this.loading = false;
            }
        )
    }

    getTicketOperationDetails(ticketId: number) {
        this.loading = true;
        this.ticketService.getTicketOperationDetails(ticketId, this.activityId, this.selectedToolId).subscribe(
            (data) => {
                this.groupings = data;

                if (this.groupings.length > 0 && this.groupings[0].ticketOperationTasks.length > 0) {
                    this.selectedTicketTaskId = !this.selectedTicketTaskId ? this.groupings[0].ticketOperationTasks[0].id :
                        this.selectedTicketTaskId;
                    this.getTaskDetails(this.selectedTicketTaskId);
                }
                this.groupings.forEach(g => {
                    g.ticketOperationTasks.forEach(tot => tot.operationTask.ticketOperationTaskId = tot.id);
                });

                this.computeTimesForOperation();
                if (this.activity) {
                    this.activity.status = AppUtils.computeOperationStatus(this.groupings);
                }

                this.loading = false;
            },
            (error) => {
                this._logger.error("Error getting ticket operation details", error);
                this.loading = false;
            }
        );
    }

    private computeTimesForOperation() {
        let totalTimeMs = 0;
        this.groupings.forEach(g => {
            g.ticketOperationTasks.forEach(tot => {
                const timeUnit = taskUnits.find(unit => unit.label === tot.operationTask.unit);
                if (timeUnit) {
                    totalTimeMs += this.getEstimatedTaskDuration(tot.operationTask, g) * timeUnit.milliseconds;
                }
            })
        });

        if (this.activity) {
            this.activity.standardTime = totalTimeMs;
            this.activity.elapsedTime = (this.activity?.status === TicketOperationStatus.NEW || !this.activity.startDate) ? 0
                : this.computeTimeSince(this.activity.startDate,
                    this.activity.status === TicketOperationStatus.RESOLVED ? this.activity.endDate : null, this.ticket.ticketEvents);
        }
    }

    computeTimeSince(startDate: string, endDate: string, ticketEvents: any) {
        const startTime = DateTimeUtils.extractTime(startDate);
        let endTime = endDate ? DateTimeUtils.extractTime(endDate) : (new Date().getTime()); // If there is no end date, use now date


        if (isEmpty(ticketEvents)) {
            return endTime - startTime;
        }

        // Extract the events that are between the startTime and endTime for my activity
        const sortedEventsBetweenDate = ticketEvents.filter(item => DateTimeUtils.extractTime(item.eventDate) > startTime
            && DateTimeUtils.extractTime(item.eventDate) < endTime)
            .sort((a, b) => DateTimeUtils.extractTime(a.eventDate) - DateTimeUtils.extractTime(b.eventDate));

        if (isEmpty(sortedEventsBetweenDate)) {
            return endTime - startTime;
        }

        // Check the last event type in the sorted array
        const lastEvent = sortedEventsBetweenDate[sortedEventsBetweenDate.length - 1]

        if (!endDate && lastEvent.eventType === EventType.Pause) {
            endTime = DateTimeUtils.extractTime(lastEvent.eventDate);
        }

        const eventTime = this._computeTotalEventTime(sortedEventsBetweenDate);

        // Return only the active time
        return endTime - startTime - eventTime;
    }

    private _computeTotalEventTime(sortedEventsBetweenDate) {
        let accumulator = 0;
        for (let i = sortedEventsBetweenDate.length - 1; i > 0; i--) {
            if (sortedEventsBetweenDate[i].eventType === EventType.Resume) {
                const currentElementEventTime = DateTimeUtils.extractTime(sortedEventsBetweenDate[i].eventDate);
                const previousElementEventTime = DateTimeUtils.extractTime(sortedEventsBetweenDate[i - 1].eventDate);
                accumulator += currentElementEventTime - previousElementEventTime;
            }
        }
        return accumulator;
    }

    getToolNames(group: any) {
        return group.ticketOperationTools.map(tot => `${tot.tool.name} - ${tot.tool.toolUid}`);
    }

    getGroupName(group: any) {
        return group.ticketOperationTools.every(tot => tot.tool.name === group.ticketOperationTools[0].tool.name)
            ? group.ticketOperationTools[0].tool.name
            : this.translate.instant('service.tickets.details.display.tools');
    }

    getTasks(group: any) {
        return group.ticketOperationTasks.map(tot => {
            const status = Object.keys(TicketOperationTaskStatus).map(key => TicketOperationTaskStatus[key]).indexOf(tot.status);
            return {
                ...tot.operationTask,
                id: tot.id,
                status: status, canStart: tot.canStart,
                requiresObservation: tot.requiresObservation ? tot.requiresObservation : tot.operationTask.requiresObservation,
                requiresSpare: tot.requiresSpare ? tot.requiresSpare : tot.operationTask.mandatorySpare,
                ticketOperationId: group.id
            };
        });
    }

    private getEstimatedTaskDuration(task: any, group: any): number {
        return task.duration * this.getGroupToolCount(group);
    }

    private getGroupToolCount(group: any): number {
        const toolUids = (group?.ticketOperationTools || [])
            .map(operationTool => operationTool.tool?.toolUid?.trim().toLowerCase())
            .filter(uid => !!uid);

        return new Set(toolUids).size;
    }

    getSelectedTaskToolCount(): number {
        if (!this.selectedTask) {
            return 0;
        }

        const group = this.groupings?.find(item =>
            item.ticketOperationTasks.some(task => task.id === this.selectedTask.id));
        return this.getGroupToolCount(group);
    }

    isTaskHighlighted(task: any) {
        return !!this.selectedTicketTaskId && this.selectedTicketTaskId === task.id;
    }

    isTaskSelected(task: any) {
        return !!this.selectedTasks.find(t => t.id === task.id);
    }

    onHighlightTask(task: any) {
        if (task.ticketOperationTaskId === this.selectedTicketTaskId) {
            this.selectedTicketTaskId = null;
            this.selectedTask = null;
            return;
        }

        this.selectedTicketTaskId = task.ticketOperationTaskId;
        this.getTaskDetails(this.selectedTicketTaskId);
    }

    onSelectTask(task: any) {
        const index = this.selectedTasks.findIndex(t => t.id === task.id);
        if (index > -1) {
            this.selectedTasks = this.selectedTasks.filter(t => t.id !== task.id);
            return;
        }

        if (this.isSelectionChanged(task)) {
            this.confirmationModal.open(task);
        } else {
            const taskIndex = this.selectedTasks.findIndex(t => t.requiresSpare || t.requiresObservation);
            if (taskIndex > -1) {
                this.selectedTasks.splice(taskIndex, 1);
            }

            this.selectedTasks.push(task);
        }
    }

    isSelectionChanged(selectedTask: any) {
        if (!this.selectedTasks || this.selectedTasks.length === 0) {
            return false;
        }
        if ((selectedTask.requiresSpare || selectedTask.requiresObservation)) {
            return true;
        }

        return !!this.selectedTasks.find(item => item.requiresSpare || item.requiresObservation);
    }

    onConfirmSelectTask(task: any) {
        this.selectedTasks = [task];
        this.confirmationModal.close();
    }

    getTaskDetails(taskId: number) {
        this.loadingTask = true;
        this.ticketService.getTicketOperationTaskById(this.ticketId, taskId).subscribe(
            (data: any) => {
                this.selectedTask = this.mapTask(data);
                this.loadingTask = false;
            },
            (error) => {
                this._logger.error("Error getting task details", error);
                this.loadingTask = false;
            }
        );
    }

    mapTask(task: any) {
        task.operationTask.photos = task.operationTask.photos.map(item => {
            return {id: item.fileId, name: item.fileName, ...item}
        });
        task.status = Object.keys(TicketOperationTaskStatus).map(key => TicketOperationTaskStatus[key]).indexOf(task.status);
        task.completedByName = task.completedBy ? `${task.completedBy.firstName} ${task.completedBy.lastName}` : 'N/A';
        return new TicketOperationTask(task);
    }

    handleDownloadDocuments(spare = false) {
        if (this.selectedTask.operationTask.files.length === 0) {
            return;
        }

        const file = this.selectedTask.operationTask.files.find(
            item => item.type === (spare ? TaskInstructionType.SPARE : TaskInstructionType.WORK));
        if (!file) {
            return;
        }

        this.taskService.downloadDocuments(this.selectedTask.operationTask.id, file.id).subscribe(
            (data: any) => {
                if (data.body != null) {
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(data.body, filename);
                }
            }
        );
    }

    handlePreviewDocuments(spare = false) {
        if (this.selectedTask.operationTask.files.length === 0) {
            return;
        }

        const file = this.selectedTask.operationTask.files.find(
            item => item.type === (spare ? TaskInstructionType.SPARE : TaskInstructionType.WORK));
        if (!file) {
            return;
        }

        this.taskService.downloadDocuments(this.selectedTask.operationTask.id, file.id).subscribe(
            (data: any) => {
                if (data.body != null) {
                    this.pdfViewerModal.open(data.url, data.headers.get('x-filename'), data.body);
                }
            }
        )

    }

    updateTicketTask(data) {
        this.ticketService.updateTicketTask(this.ticketId, data.ticketTaskId, data.requiresObservation, data.requiresSpare)
            .subscribe(() => {
                    // Update info on selected tasks array
                    const selectedTask = this.selectedTasks ? this.selectedTasks.find(t => t.id === data.ticketTaskId) : null;
                    if (selectedTask) {
                        if ((data.requiresSpare || data.requiresObservation) && this.selectedTasks.length > 1) {
                            this.selectedTasks = this.selectedTasks.filter(t => t.id !== selectedTask.id);
                        }
                        selectedTask.requiresSpare = data.requiresSpare;
                        selectedTask.requiresObservation = data.requiresObservation;
                    }

                    // Update info on grouping
                    this.groupings.forEach(g => {
                        const task = g.ticketOperationTasks.find(item => item.id === data.ticketTaskId);
                        if (task) {
                            task.requiresSpare = data.requiresSpare;
                            task.requiresObservation = data.requiresObservation;
                        }
                    });

                    this.toastr.success(this.translate.instant('service.tickets.details.activityDetails.taskUpdateSuccess'));
                },
                (error: any) => {
                    this._logger.error("Error updating ticket task", error);
                    this.toastr.error(this.translate.instant('service.tickets.details.activityDetails.taskUpdateError'));
                });
    }

    computeStandardTime(task) {
        return this.translate.instant(
            'service.tickets.details.activityDetails.standardTime') + ` ${task.duration}${task.unit.charAt(0).toLowerCase()}`;
    }

    onTimerStart() {
        if (this.readonly) {
            return;
        }
        this.badgeConfirmationModal.open();
    }

    onRevisionInfoClick() {
        this.loading = true;
        this.ticketService.getTicketProductionData(this.ticketId, this.activityId)
            .subscribe((data: RevisionInfo[]) => {
                this.operationRevisionInfoModal.open(data);
                this.loading = false;
            });
    }

    onStartOperation(event: any) {
        this.ticketService.startOperation(this.ticket, this.activity.operationId, event.boschId).subscribe(
            (data) => {
                this.badgeConfirmationModal.close();
                this.operation?.timer?.startTimer();
                this.toastr.success(this.translate.instant('service.tickets.details.activityDetails.operationStarted',
                    {currentOperationName: this.activity.name, ticketId: this.ticketId}
                ));

                this.activity = data.find(op => op.id === this.activity.id);
                this.getTicketOperationDetails(this.ticketId);
            },
            (error) => {
                this._logger.error("Error starting operation", error);
                this.badgeConfirmationModal.close();
            }
        )
    }

    finishTask(action: string) {
        this.finishedGroups = [];
        this.nextTaskAction = action;
        const unfinishedGroups = this.groupings.filter(g => !g.ticketOperationTasks.every(
            task => task.status !== TicketOperationTaskStatus.NEW));

        unfinishedGroups.forEach(g => {
            let toBeFinishedTask = 0;
            g.ticketOperationTasks.forEach(tot => {
                if (tot.status !== TicketOperationTaskStatus.NEW || this.selectedTasks.find(item => item.id === tot.id)) {
                    toBeFinishedTask++;
                }
            });
            if (toBeFinishedTask === g.ticketOperationTasks.length) {
                this.finishedGroups.push(g);
            }
        });

        if (this.finishedGroups.length > 0) {
            this.finishGroupModal.open(this.finishedGroups);
        } else if (action === 'done') {
            this.markTasksAsDone();
        } else {
            this.markTasksAsNotDone();
        }
    }

    markTasksAsDone() {
        if (this.selectedTasks.length === 0) {
            this.toastr.error(this.translate.instant('service.tickets.details.activityDetails.noTasksSelected'));
            return;
        }

        if (this.selectedTasks.find(t => t.requiresObservation)) {
            this.taskObservationModal.open();
        } else if (this.selectedTasks.find(t => t.requiresSpare)) {
            this.taskSpareQuantityModal.open();
        } else {
            this.markTaskDoneModal.open();
        }
    }

    onSubmitObservation(event: any) {
        this.taskObservationModal.close();
        this.comment = event;

        if (this.selectedTasks.find(t => t.requiresSpare)) {
            this.taskSpareQuantityModal.open();
        } else {
            this.markTaskDoneModal.open();
        }
    }

    onSubmitSpareQuantity(event: any) {
        this.taskSpareQuantityModal.close();

        if (!this.form.controls.spareQuantity.value) {
            this.showValidations = true;
            return;
        }

        this.showValidations = false;
        this.spareQuantity = this.form.controls.spareQuantity.value;
        this.form.reset();

        this.markTaskDoneModal.open();
    }

    onConfirmMarkTasksDone(event: any) {
        this.markTaskDoneModal.close();
        this.boschId = event.boschId

        if (this.finishedGroups.length > 0 && this.openNewTicket) {
            this.openExtractTicketModal();
        } else {
            this.finishTaskAsDone();
        }
    }

    finishTaskAsDone() {
        this.loading = true;
        this.ticketService.finishTasks(this.ticketId, this.selectedTasks, this.comment, this.spareQuantity, this.boschId,
            TicketOperationTaskStatus.RESOLVED)
            .subscribe(
                (data: any[]) => {
                    this.toastr.success(this.translate.instant(
                        'service.tickets.details.activityDetails.tasksMarkAsDone'));
                    if (this.finishedGroups.length > 0) {
                        this.finishOperations();
                    } else {
                        this.computeTimesForOperation();
                        this.loading = false;
                    }

                    if (this.selectedTicketTaskId) {
                        this.getTaskDetails(this.selectedTicketTaskId);
                    }

                    this.computeTaskStatuses(data);
                },
                (error) => {
                    this._logger.error("Error finishing task as done", error);
                    this.loading = false;
                }
            );
    }

    markTasksAsNotDone() {
        if (this.selectedTasks.length === 0) {
            this.toastr.error(this.translate.instant('service.tickets.details.activityDetails.noTasksSelected'));
            return;
        }
        this.notDoneReasonModal.open();
    }

    onReasonMarkTasksAsNotDone(event: any) {
        this.notDoneReasonModal.close();
        this.notDoneReason = event;

        if (this.finishedGroups.length > 0 && this.openNewTicket) {
            this.tableConfig = new TableConfig();
            this.tableConfig.upsertFilter("ticketId", this.ticketId);
            this.ticketCreateModal.open(null, null, this.ticket.mapOperationTypes(this.ticket.ticketOperations));
        } else {
            this.markTasksNotDoneModal.open();
        }
    }

    onConfirmMarkTasksNotDone(event: any) {
        this.markTasksNotDoneModal.close();
        this.boschId = event.boschId;

        this.finishTaskAsNotDone();
    }

    finishTaskAsNotDone() {
        this.loading = true;
        this.ticketService.finishTasks(this.ticketId, this.selectedTasks, null, null, this.boschId,
            TicketOperationTaskStatus.UNRESOLVED, this.notDoneReason).subscribe(
            (data: any[]) => {
                this.toastr.success(this.translate.instant(
                    'service.tickets.details.activityDetails.tasksMarkAsNotDone'));
                if (this.finishedGroups.length > 0) {
                    this.finishOperations();
                } else {
                    this.computeTimesForOperation();
                    this.loading = false;
                }

                if (this.selectedTicketTaskId) {
                    this.getTaskDetails(this.selectedTicketTaskId);
                }

                this.computeTaskStatuses(data);
            },
            (error) => {
                this._logger.error("Error finishing task as NOT DONE", error);
                this.loading = false;
            }
        );
    }

    onConfirmFinishGroup(event: any) {
        this.openNewTicket = this.form.value.newTicket
        this.finishGroupModal.close();

        if (this.nextTaskAction === 'done') {
            this.markTasksAsDone();
        } else {
            this.markTasksAsNotDone();
        }
    }

    openExtractTicketModal() {
        this.tableConfig = new TableConfig();
        this.tableConfig.upsertFilter("ticketId", this.ticketId);
        this.ticketCreateModal.open(null, null, this.ticket.mapOperationTypes(this.ticket.ticketOperations));
    }

    onExtractTicket(ticket) {
        this.ticketCreateModal.close();
        this.loading = true;
        this.ticketService.extractTicket(this.ticketId, ticket, this.boschId, null).subscribe(
            (data: any) => {
                this.loading = false;

                if (this.nextTaskAction === 'done') {
                    this.finishTaskAsDone();
                } else {
                    this.finishTaskAsNotDone();
                }

                const operations = ticket.operationTypes.map(op => op.name).join(", ");
                this.toastr.info(this.buildUpdateTicketMessage(operations, data.id));
            },
            (error) => {
                this._logger.error("Error extracting new ticket", error);

                this.loading = false;
            }
        )
    }


    finishOperations() {
        this.loading = true;
        this.ticketService.finishOperations(this.ticketId, this.activity.operationId,
            null, null, this.boschId).subscribe(
            (data: any[]) => {
                this.loading = false;

                this.getTicketOperationDetails(this.ticketId);

                this.nextOperations = data;
                this.computeTimesForOperation();
                this.showGroupsCompleteMessage(data);
            },
            (error) => {
                this._logger.error("Error finishing operation", error);

                this.loading = false;
            }
        )
    }

    onConfirmGroupComplete() {
        this.groupCompleteConfirmationModal.close();
    }

    computeTaskStatuses(data: any) {
        this.groupings.forEach(g => {
            g.ticketOperationTasks.forEach(tot => {
                const index = data.findIndex(item => item.id === tot.id);
                if (index > -1) {
                    tot.status = data[index].status;
                }
            });
        });
        this.selectedTasks = [];
    }

    computeCanStart(group: any) {
        return group.ticketOperationTasks.some(tot => tot.canStart === false);
    }

    computeCanReturn(group: any) {
        return group.ticketOperationTools?.some(tool => tool.canBeReturned === false);
    }

    buildUpdateTicketMessage(operations, ticketId) {
        const message = this.translate.instant('service.tickets.details.toasts.operationsAddedToTicket');
        return this.translateParser.interpolate(message, {ticketId: ticketId, operations: operations});
    }

    computeInfoMessage(selectedTask: any) {
        if (selectedTask.previousTicketId) {
            return this.translate.instant('service.tickets.details.tasks.info-message',
                {ticketId: selectedTask.previousTicketId});
        }
    }

    showGroupsCompleteMessage(nextOperations: NextTicketOperation[]) {
        let showConfirmationDialog = false;
        if (nextOperations && nextOperations.length > 0) {
            let message = this.translate.instant('service.tickets.details.activityDetails.tasksCompletedForToolBatch',
                {operationName: this.activity ? this.activity.name : ''});
            message += '\n';

            for (let i = 0; i < this.nextOperations.length; i++) {
                message += '\n';
                if (this.nextOperations[i].nextTicketOperation) {
                    message += this.translate.instant('service.tickets.details.activityDetails.moveToolsToOperation',
                        {
                            nextOperationName: this.nextOperations[i].nextTicketOperation?.name
                        });
                } else if (this.nextOperations[i].nextTicketOperation && this.ticket.hasMaintenanceAtLine) {
                    message += this.translate.instant('service.tickets.details.activityDetails.moveTools',
                        {
                            nextOperationName: this.nextOperations[i].nextTicketOperation?.name
                        });
                } else if (this.nextOperations[i].isTicketClosed) {
                    message += this.translate.instant('service.tickets.details.activityDetails.moveToolToAtom');
                    showConfirmationDialog = true;
                } else {
                    message += this.translate.instant('service.tickets.details.activityDetails.toolsCanBeReturn');
                }
            }
            message += '\n';
            if (showConfirmationDialog) {
                this.groupCompletedMessage = message;
                this.groupCompleteConfirmationModal.open();
            } else {
                this.toastr.success(message);
            }
        }
    }

    canActivityBeStarted() {
        return this.needToScanTools() && !ToolService.allToolsAreScaned(this.ticket.ticketTools);
    }

    needToScanTools() {
        const firstOperation = this.ticket.ticketOperations[0] && this.ticket.ticketOperations[0].status === 'New';
        return firstOperation && this.ticket.hasMaintenanceAtLine;
    }

    createReturn(group: any) {
        this.selectedGroupForReturn = group;
        this.badgeForReturnConfirmationModal.open();
    }

    onReturnToolsConfirmed(event: any) {
        this.ticketService.returnTools(this.ticket.id, this.activity.operationId,
            this.selectedGroupForReturn.id, event.boschId).subscribe(
            (data) => {
                this.selectedGroupForReturn = null;

                this.toastr.success(
                    this.translate.instant('service.tickets.details.activityDetails.returnToolsModal.success',
                        {toolReturnId: data.id}))

                this.badgeForReturnConfirmationModal.close();
                this.getTicketOperationDetails(this.ticketId);
            },
            (error) => {
                this._logger.error("Error creating return for tools", error);

                this.selectedGroupForReturn = null;
                this.toastr.error(
                    this.translate.instant('service.tickets.details.activityDetails.returnToolsModal.failed'));
                this.badgeForReturnConfirmationModal.close();
            }
        )
    }

    isReturnedPossible() {
        return !this.ticket.hasMaintenanceAtLine;
    }
}
